function plain(value) {
  return String(value ?? '')
    .replace(/[—–]/g, '-')
    .replace(/[·•]/g, '|')
    .replace(/[^\x20-\x7E]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function pdfText(value) {
  return plain(value)
    .replace(/\\/g, '\\\\')
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)');
}

function measure(value, size) {
  return plain(value).length * size * 0.5;
}

function wrap(value, size, maxWidth) {
  const words = plain(value).split(' ').filter(Boolean);
  const lines = [];
  let line = '';
  words.forEach((word) => {
    const next = line ? `${line} ${word}` : word;
    if (line && measure(next, size) > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = next;
    }
  });
  if (line) lines.push(line);
  return lines.length ? lines : [''];
}

function fitLine(value, size, maxWidth) {
  const text = plain(value);
  if (!text) return '';
  if (measure(text, size) <= maxWidth) return text;
  let out = text;
  while (out.length > 1 && measure(`${out}...`, size) > maxWidth) out = out.slice(0, -1);
  return `${out.replace(/\s+\S*$/, '')}...`;
}

function buildPdf(objects) {
  let out = '%PDF-1.4\n';
  const offsets = [0];
  objects.forEach((body, index) => {
    offsets.push(out.length);
    out += `${index + 1} 0 obj\n${body}\nendobj\n`;
  });
  const startxref = out.length;
  out += `xref\n0 ${objects.length + 1}\n`;
  out += '0000000000 65535 f \n';
  offsets.slice(1).forEach((offset) => {
    out += `${String(offset).padStart(10, '0')} 00000 n \n`;
  });
  out += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${startxref}\n%%EOF`;
  return out;
}

const BRICK = '0.651 0.216 0.149';
const INK = '0.161 0.153 0.141';
const MUTED = '0.420 0.392 0.373';
const GREEN = '0.184 0.416 0.290';
const GOLD = '0.667 0.506 0.208';
const RED = '0.706 0.325 0.263';
const WHITE = '1 1 1';
const PAPER = '0.976 0.969 0.961';
const RULE = '0.890 0.863 0.843';

function toneRgb(tone) {
  if (tone === 'good') return GREEN;
  if (tone === 'wait') return GOLD;
  if (tone === 'stop') return RED;
  return BRICK;
}

function tally(rows, noun) {
  if (!rows.length) return `No ${noun} on this bid.`;
  const open = rows.filter((row) => row.tone === 'stop').length;
  const watch = rows.filter((row) => row.tone === 'wait').length;
  const notes = [];
  if (open) notes.push(`${open} still to pin`);
  if (watch) notes.push(`${watch} to review`);
  return notes.length ? `${rows.length} ${noun}, ${notes.join(', ')}` : `${rows.length} ${noun}, all current`;
}

export function downloadBidPackPdf({
  fileName,
  firm,
  legalName,
  reference,
  title,
  client,
  service,
  generated,
  description,
  facts,
  credentials,
  people
}) {
  const pageWidth = 595;
  const pageHeight = 842;
  const left = 40;
  const right = 555;
  const inner = right - left;
  const firmName = legalName || firm || 'Dhruv Consultancy';
  const streams = [];
  let ops = [];
  let y = 748;
  let pageNo = 1;

  const text = (x, baseline, size, font, value, color = INK) => {
    const line = pdfText(value);
    if (!line) return;
    ops.push(`BT /${font} ${size} Tf ${color} rg 1 0 0 1 ${x.toFixed(2)} ${baseline.toFixed(2)} Tm (${line}) Tj ET`);
  };

  const fill = (x, boxY, w, h, color) => {
    ops.push(`${color} rg ${x.toFixed(2)} ${boxY.toFixed(2)} ${w.toFixed(2)} ${h.toFixed(2)} re f`);
  };

  const paintFooter = () => {
    fill(0, 0, pageWidth, 34, PAPER);
    fill(0, 34, pageWidth, 2, BRICK);
    text(left, 14, 8, 'F1', `${firmName}  |  Bid pack  |  ${generated || ''}`, MUTED);
    const label = String(pageNo);
    text(right - measure(label, 8), 14, 8, 'F1', label, MUTED);
  };

  const paintContinuation = () => {
    fill(0, pageHeight - 28, pageWidth, 28, BRICK);
    text(left, pageHeight - 18, 9, 'F2', fitLine(`${reference || 'Tender'}  |  Bid pack, continued`, 9, inner - 40), WHITE);
    y = pageHeight - 52;
  };

  const flush = () => {
    paintFooter();
    streams.push(ops.join('\n'));
    ops = [];
    pageNo += 1;
    paintContinuation();
  };

  const ensure = (height) => {
    if (y - height < 56) flush();
  };

  fill(0, pageHeight - 72, pageWidth, 72, BRICK);
  text(left, 804, 13, 'F2', fitLine(firmName, 13, inner - 90), WHITE);
  text(right - measure('Bid pack', 11), 804, 11, 'F2', 'Bid pack', WHITE);
  text(left, 784, 8, 'F1', 'Credentials and people named on this pursuit', WHITE);

  text(left, y, 10, 'F2', reference || 'Tender', BRICK);
  y -= 22;
  wrap(title || 'Untitled pursuit', 17, inner).slice(0, 3).forEach((line) => {
    text(left, y, 17, 'F2', line);
    y -= 22;
  });
  y -= 2;
  text(left, y, 10, 'F1', [client, service].filter(Boolean).join('  |  ') || 'Client not recorded', MUTED);
  y -= 16;
  const about = wrap(description || '', 9, inner).slice(0, 3).filter((line) => plain(line));
  if (about.length) {
    about.forEach((line) => {
      text(left, y, 9, 'F1', line, INK);
      y -= 13;
    });
    y -= 6;
  }
  y -= 4;

  const shownFacts = (Array.isArray(facts) ? facts : []).filter((item) => plain(item?.value)).slice(0, 4);
  if (shownFacts.length) {
    const gap = 8;
    const boxW = (inner - gap * (shownFacts.length - 1)) / shownFacts.length;
    const boxH = 42;
    ensure(boxH + 8);
    shownFacts.forEach((fact, index) => {
      const x = left + index * (boxW + gap);
      fill(x, y - boxH, boxW, boxH, PAPER);
      fill(x, y - 2, boxW, 2, BRICK);
      text(x + 8, y - 16, 7, 'F2', String(fact.label || '').toUpperCase(), MUTED);
      text(x + 8, y - 32, 9, 'F2', fitLine(fact.value, 9, boxW - 16));
    });
    y -= boxH + 18;
  }

  const summary = [tally(credentials || [], 'credentials'), tally(people || [], 'people')].join('   |   ');
  text(left, y, 9, 'F1', summary, MUTED);
  y -= 22;

  const drawEntry = (row) => {
    const status = plain(row.status) || '-';
    const statusW = Math.max(measure(status, 9), 72);
    const nameWidth = Math.max(180, inner - statusW - 28);
    const nameLines = wrap(row.name || '-', 10, nameWidth).slice(0, 2);
    const pinnedText = /^not pinned$/i.test(plain(row.pinned)) ? 'Not pinned' : `Pinned ${plain(row.pinned) || '-'}`;
    const meta = [plain(row.detail), pinnedText, `Library ${plain(row.library) || '-'}`].filter(Boolean).join('   |   ');
    const metaLines = wrap(meta, 8, inner - 20).slice(0, 2);
    const height = 16 + nameLines.length * 13 + metaLines.length * 11;
    ensure(height + 8);
    const boxBottom = y + 11 - height;
    fill(left, boxBottom, inner, height, PAPER);
    fill(left, boxBottom, 3, height, toneRgb(row.tone));
    nameLines.forEach((line, lineIndex) => {
      text(left + 12, y - lineIndex * 13, 10, 'F2', line);
    });
    text(right - measure(status, 9) - 10, y, 9, 'F2', status, toneRgb(row.tone));
    metaLines.forEach((line, lineIndex) => {
      text(left + 12, y - nameLines.length * 13 - 2 - lineIndex * 11, 8, 'F1', line, MUTED);
    });
    y = boxBottom - 8;
  };

  const section = (heading, note, rows) => {
    const list = Array.isArray(rows) ? rows : [];
    ensure(64);
    const count = String(list.length).padStart(2, '0');
    text(left, y, 13, 'F2', heading);
    text(right - measure(count, 13), y, 13, 'F2', count, BRICK);
    y -= 15;
    text(left, y, 8, 'F1', note, MUTED);
    y -= 8;
    fill(left, y, inner, 0.8, RULE);
    y -= 16;
    if (!list.length) {
      text(left, y, 10, 'F1', 'None recorded.', MUTED);
      y -= 24;
      return;
    }
    list.forEach(drawEntry);
    y -= 8;
  };

  section(
    'Credentials',
    'The version pinned for this bid, beside the version now in the library.',
    credentials
  );
  section(
    'People named',
    'The CV pinned for this bid, beside the version now on the roster.',
    people
  );

  if (ops.length) {
    paintFooter();
    streams.push(ops.join('\n'));
  }

  const font1 = 3 + streams.length * 2;
  const font2 = font1 + 1;
  const pageRefs = streams.map((_, index) => `${4 + index * 2} 0 R`).join(' ');
  const objects = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    `<< /Type /Pages /Count ${streams.length} /Kids [${pageRefs}] >>`
  ];
  streams.forEach((stream, index) => {
    const contentId = 3 + index * 2;
    objects.push(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`);
    objects.push(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Contents ${contentId} 0 R /Resources << /Font << /F1 ${font1} 0 R /F2 ${font2} 0 R >> >> >>`);
  });
  objects.push('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>');
  objects.push('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>');

  const blob = new Blob([buildPdf(objects)], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}
